import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, Send, CheckCircle, Upload, X, Loader2, Tv } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const REL_OPTIONS = [
  { value: "owner", fr: "Propriétaire de la chaîne", en: "Channel Owner" },
  { value: "representative", fr: "Représentant autorisé", en: "Authorized Representative" },
  { value: "employee", fr: "Employé", en: "Employee" },
  { value: "viewer", fr: "Téléspectateur / Fan", en: "Viewer or Fan" },
  { value: "other", fr: "Autre", en: "Other" },
];

const EMPTY = {
  submitter_name: "", submitter_email: "", submitter_phone: "", relationship: "viewer",
  channel_name: "", channel_website: "", country_id: "", category_id: "",
  primary_language: "", description_fr: "", description_en: "",
  logo_url: "", cover_url: "", document_url: "",
  stream_url: "", backup_stream_url: "",
  social_links: { facebook: "", youtube: "", instagram: "", x: "" },
  authorization_confirmed: false, additional_notes: "", terms_accepted: false,
  website: "", // honeypot
};

function UploadField({ label, hint, value, onChange, kind = "image", testid }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/public/submit-channel/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-[#94A3B8] mb-1.5 block">{label}</label>
      {value ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0F172A] border border-[#FFC928]/30" data-testid={`${testid}-preview`}>
          {kind === "image" ? (
            <img src={value.startsWith("http") ? value : value} alt="" className="w-12 h-12 rounded-lg object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-[#FFC928]/10 flex items-center justify-center text-[#FFC928] text-xs font-bold">PDF</div>
          )}
          <span className="flex-1 text-sm text-white truncate">Uploaded</span>
          <button type="button" onClick={() => onChange("")} className="text-[#94A3B8] hover:text-red-400 transition-colors">
            <X size={18} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid={testid}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0F172A] border border-dashed border-white/[0.15] text-[#94A3B8] text-sm hover:border-[#FFC928]/40 hover:text-white transition-colors">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading..." : (hint || "Upload file")}
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden"
        accept={kind === "document" ? "application/pdf" : "image/webp,image/jpeg,image/png"}
        onChange={handle} />
      {err && <p className="text-xs text-red-400 mt-1">{err}</p>}
    </div>
  );
}

export default function SubmitChannel() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const T = (fr, en) => (lang === "fr" ? fr : en);

  const [form, setForm] = useState(EMPTY);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/countries`).then((r) => setCountries(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setForm((p) => ({ ...p, social_links: { ...p.social_links, [k]: v } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.terms_accepted) { setError(T("Veuillez accepter les conditions.", "Please accept the terms.")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/submit-channel`, form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setError(e2.response?.data?.detail || T("Une erreur est survenue.", "Something went wrong."));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-[#0F172A] border border-white/[0.06] text-white text-sm placeholder-[#94A3B8]/40 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/30 focus:border-[#FFC928]/30";
  const labelCls = "text-xs font-medium text-[#94A3B8] mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#050814]" data-testid="submit-channel-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <nav className="flex items-center gap-2 text-sm text-[#94A3B8] mb-8">
          <Link to="/" className="hover:text-white transition-colors">{T("Accueil", "Home")}</Link>
          <ChevronRight size={14} />
          <span className="text-[#FFC928]">{T("Soumettre une chaîne", "Submit a Channel")}</span>
        </nav>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-2xl bg-[#FFC928]/10 flex items-center justify-center text-[#FFC928]">
            <Tv size={22} />
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "Poppins" }} data-testid="submit-channel-title">
            {T("Soumettre une chaîne", "Submit a Channel")}
          </motion.h1>
        </div>
        <p className="text-[#94A3B8] text-sm leading-relaxed mb-8 max-w-2xl">
          {T(
            "Proposez une chaîne de télévision à ajouter sur Djilu.com. Notre équipe examinera votre demande avant toute publication.",
            "Suggest a television channel for inclusion on Djilu.com. Our team will review your submission before publication."
          )}
        </p>

        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-green-500/20 bg-green-500/5 p-8 text-center" data-testid="submit-channel-success">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <p className="text-white font-semibold text-lg mb-2">
              {T("Merci. Votre proposition de chaîne a été envoyée et sera examinée par notre équipe.",
                 "Thank you. Your channel submission has been received and will be reviewed by our team.")}
            </p>
            <Link to="/" className="inline-block mt-4 text-[#FFC928] text-sm hover:underline">{T("Retour à l'accueil", "Back to home")}</Link>
          </motion.div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            onSubmit={handleSubmit} className="space-y-8" data-testid="submit-channel-form">

            {/* Honeypot (hidden) */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off"
              value={form.website} onChange={(e) => set("website", e.target.value)}
              className="hidden" aria-hidden="true" />

            {/* Submitter */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-lg">{T("Vos informations", "Your Information")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{T("Nom du soumetteur", "Submitter Name")} *</label>
                  <input required data-testid="sc-name" className={inputCls} value={form.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("Adresse e-mail", "Email Address")} *</label>
                  <input required type="email" data-testid="sc-email" className={inputCls} value={form.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("Numéro de téléphone (facultatif)", "Phone Number (optional)")}</label>
                  <input data-testid="sc-phone" className={inputCls} value={form.submitter_phone} onChange={(e) => set("submitter_phone", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("Relation avec la chaîne", "Relationship to Channel")} *</label>
                  <select required data-testid="sc-relationship" className={inputCls + " cursor-pointer"} value={form.relationship} onChange={(e) => set("relationship", e.target.value)}>
                    {REL_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-[#0F172A]">{T(o.fr, o.en)}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Channel */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-lg">{T("Détails de la chaîne", "Channel Details")}</h2>
              <div>
                <label className={labelCls}>{T("Nom de la chaîne", "Channel Name")} *</label>
                <input required data-testid="sc-channel-name" className={inputCls} value={form.channel_name} onChange={(e) => set("channel_name", e.target.value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{T("Site web de la chaîne", "Channel Website")}</label>
                  <input type="url" placeholder="https://" data-testid="sc-website" className={inputCls} value={form.channel_website} onChange={(e) => set("channel_website", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("Langue principale", "Primary Language")}</label>
                  <input data-testid="sc-language" className={inputCls} value={form.primary_language} onChange={(e) => set("primary_language", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("Pays", "Country")}</label>
                  <select data-testid="sc-country" className={inputCls + " cursor-pointer"} value={form.country_id} onChange={(e) => set("country_id", e.target.value)}>
                    <option value="" className="bg-[#0F172A]">{T("Sélectionner…", "Select…")}</option>
                    {countries.map((c) => <option key={c.id} value={c.id} className="bg-[#0F172A]">{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{T("Catégorie", "Category")}</label>
                  <select data-testid="sc-category" className={inputCls + " cursor-pointer"} value={form.category_id} onChange={(e) => set("category_id", e.target.value)}>
                    <option value="" className="bg-[#0F172A]">{T("Sélectionner…", "Select…")}</option>
                    {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#0F172A]">{c.name_en || c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>{T("Description de la chaîne", "Channel Description")}</label>
                <textarea rows={4} data-testid="sc-description" className={inputCls + " resize-none"} value={form.description_en} onChange={(e) => { set("description_en", e.target.value); set("description_fr", e.target.value); }} />
              </div>
            </section>

            {/* Media */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-lg">{T("Médias", "Media")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <UploadField testid="sc-logo-upload" kind="image" value={form.logo_url} onChange={(v) => set("logo_url", v)}
                  label={T("Logo officiel", "Official Logo")} hint={T("Téléverser le logo (WebP/JPG/PNG)", "Upload logo (WebP/JPG/PNG)")} />
                <UploadField testid="sc-cover-upload" kind="image" value={form.cover_url} onChange={(v) => set("cover_url", v)}
                  label={T("Image de couverture (facultatif)", "Cover Image (optional)")} hint={T("Téléverser une image", "Upload an image")} />
                <div className="sm:col-span-2">
                  <UploadField testid="sc-doc-upload" kind="document" value={form.document_url} onChange={(v) => set("document_url", v)}
                    label={T("Document justificatif (facultatif, PDF)", "Supporting Document (optional, PDF)")} hint={T("Téléverser un PDF", "Upload a PDF")} />
                </div>
              </div>
            </section>

            {/* Streams */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-lg">{T("Flux de diffusion", "Stream URLs")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{T("URL du flux public", "Public Stream URL")}</label>
                  <input type="url" placeholder="https://…m3u8" data-testid="sc-stream" className={inputCls} value={form.stream_url} onChange={(e) => set("stream_url", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T("URL de secours (facultatif)", "Backup Stream URL (optional)")}</label>
                  <input type="url" placeholder="https://" data-testid="sc-backup-stream" className={inputCls} value={form.backup_stream_url} onChange={(e) => set("backup_stream_url", e.target.value)} />
                </div>
              </div>
            </section>

            {/* Social + notes */}
            <section className="space-y-4">
              <h2 className="text-white font-semibold text-lg">{T("Réseaux sociaux & notes", "Social & Notes")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["facebook", "youtube", "instagram", "x"].map((s) => (
                  <div key={s}>
                    <label className={labelCls + " capitalize"}>{s === "x" ? "X (Twitter)" : s}</label>
                    <input placeholder="https://" data-testid={`sc-social-${s}`} className={inputCls} value={form.social_links[s]} onChange={(e) => setSocial(s, e.target.value)} />
                  </div>
                ))}
              </div>
              <div>
                <label className={labelCls}>{T("Notes supplémentaires", "Additional Notes")}</label>
                <textarea rows={3} data-testid="sc-notes" className={inputCls + " resize-none"} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} />
              </div>
            </section>

            {/* Consent */}
            <section className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="sc-authorization">
                <input type="checkbox" className="mt-1 accent-[#FFC928]" checked={form.authorization_confirmed} onChange={(e) => set("authorization_confirmed", e.target.checked)} />
                <span className="text-sm text-[#A1A9B8]">{T(
                  "Je confirme être propriétaire de la chaîne ou avoir l'autorisation de la soumettre.",
                  "I confirm that I own the channel or am authorized to submit it.")}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="sc-terms">
                <input type="checkbox" required className="mt-1 accent-[#FFC928]" checked={form.terms_accepted} onChange={(e) => set("terms_accepted", e.target.checked)} />
                <span className="text-sm text-[#A1A9B8]">{T(
                  "J'accepte les conditions d'utilisation et la politique de confidentialité.",
                  "I accept the terms of use and privacy policy.")} *</span>
              </label>
            </section>

            {error && <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-400" data-testid="sc-error">{error}</div>}

            <button type="submit" disabled={submitting} data-testid="sc-submit"
              className="flex items-center gap-2 px-8 py-3 bg-[#FFC928] text-black font-bold text-sm rounded-xl hover:bg-[#FFD54F] transition-all shadow-[0_0_20px_rgba(255,201,40,0.2)] disabled:opacity-60">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {T("Envoyer la proposition", "Submit Channel")}
            </button>
          </motion.form>
        )}
      </div>
    </div>
  );
}
